import {
  IDepot, IBac, IPlanningEntry, ContentType, IRestriction,
  IClient, IDepotGeo, ICapacite, IClientSynthese,
  IBacAction, IBacActionCreatePayload,
} from '../../models';
import { IDataProvider } from './IDataProvider';
import { DEFAULT_PRODUCTS_CONFIG, IProductsConfig } from '../../config/productsConfig';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const mockData: {
  depots: IDepot[];
  bacs: IBac[];
  planning: IPlanningEntry[];
  restrictions: IRestriction[];
} = require('../../webparts/gestionBacs/services/mockData.json');

// eslint-disable-next-line @typescript-eslint/no-var-requires
const clientsData: {
  clients: IClient[];
  depotsGeo: IDepotGeo[];
  capacites: ICapacite[];
  synthese: IClientSynthese[];
} = require('../../webparts/gestionBacs/services/clientsMockData.json');

function delay<T>(value: T): Promise<T> {
  return new Promise(resolve => setTimeout(() => resolve(value), 100));
}

export class MockDataProvider implements IDataProvider {
  private depots: IDepot[] = [...mockData.depots];
  private bacs: IBac[] = [...mockData.bacs];
  private planning: IPlanningEntry[] = [...mockData.planning];
  private restrictions: IRestriction[] = [...mockData.restrictions];
  private clients: IClient[] = [...clientsData.clients];
  private depotsGeo: IDepotGeo[] = [...clientsData.depotsGeo];
  private capacites: ICapacite[] = [...clientsData.capacites];
  private synthese: IClientSynthese[] = [...clientsData.synthese];
  private productsConfig: IProductsConfig = DEFAULT_PRODUCTS_CONFIG;
  private nextId = 100000;

  private genId(): number { return this.nextId++; }

  // ---- Depots ----
  getDepots(): Promise<IDepot[]> { return delay([...this.depots]); }
  addDepot(title: string): Promise<IDepot> {
    const d: IDepot = { Id: this.genId(), Title: title };
    this.depots.push(d);
    return delay(d);
  }
  updateDepot(id: number, title: string): Promise<void> {
    const idx = this.depots.findIndex(d => d.Id === id);
    if (idx >= 0) this.depots[idx] = { ...this.depots[idx], Title: title };
    return delay(undefined);
  }
  deleteDepot(id: number): Promise<void> {
    this.depots = this.depots.filter(d => d.Id !== id);
    return delay(undefined);
  }

  // ---- Bacs ----
  getBacs(): Promise<IBac[]> { return delay([...this.bacs]); }
  getBacsByDepot(depotId: number): Promise<IBac[]> {
    return delay(this.bacs.filter(b => b.DepotId === depotId));
  }
  addBac(bac: Omit<IBac, 'Id' | 'DepotTitle'>): Promise<number> {
    const id = this.genId();
    const depot = this.depots.find(d => d.Id === bac.DepotId);
    this.bacs.push({ ...bac, Id: id, DepotTitle: depot ? depot.Title : '' });
    return delay(id);
  }
  updateBac(id: number, updates: Partial<IBac>): Promise<void> {
    const idx = this.bacs.findIndex(b => b.Id === id);
    if (idx >= 0) this.bacs[idx] = { ...this.bacs[idx], ...updates };
    return delay(undefined);
  }
  deleteBac(id: number): Promise<void> {
    this.bacs = this.bacs.filter(b => b.Id !== id);
    return delay(undefined);
  }

  // ---- Planning ----
  getPlanningEntries(bacIds?: number[]): Promise<IPlanningEntry[]> {
    let result = [...this.planning];
    if (bacIds && bacIds.length > 0) {
      result = result.filter(e => bacIds.indexOf(e.BacId) >= 0);
    }
    return delay(result);
  }
  upsertPlanningEntry(bacId: number, mois: string, contenu: string, typeContenu: ContentType): Promise<void> {
    const title = `${bacId}-${mois.substring(0, 7)}`;
    const idx = this.planning.findIndex(e => e.Title === title);
    if (idx >= 0) {
      this.planning[idx] = { ...this.planning[idx], Contenu: contenu, TypeContenu: typeContenu };
    } else {
      this.planning.push({
        Id: this.genId(), Title: title, BacId: bacId, Mois: mois,
        Contenu: contenu, TypeContenu: typeContenu,
      });
    }
    return delay(undefined);
  }
  deletePlanningEntry(id: number): Promise<void> {
    this.planning = this.planning.filter(e => e.Id !== id);
    return delay(undefined);
  }

  // ---- Restrictions ----
  getRestrictions(): Promise<IRestriction[]> { return delay([...this.restrictions]); }
  addRestriction(r: Omit<IRestriction, 'Id' | 'DepotTitle'>): Promise<number> {
    const id = this.genId();
    const depot = this.depots.find(d => d.Id === r.DepotId);
    this.restrictions.push({ ...r, Id: id, DepotTitle: depot ? depot.Title : '' });
    return delay(id);
  }
  updateRestriction(id: number, updates: Partial<IRestriction>): Promise<void> {
    const idx = this.restrictions.findIndex(r => r.Id === id);
    if (idx >= 0) this.restrictions[idx] = { ...this.restrictions[idx], ...updates };
    return delay(undefined);
  }
  deleteRestriction(id: number): Promise<void> {
    this.restrictions = this.restrictions.filter(r => r.Id !== id);
    return delay(undefined);
  }

  // ---- Clients dashboard ----
  getClients(): Promise<IClient[]> { return delay([...this.clients]); }
  getDepotsGeo(): Promise<IDepotGeo[]> { return delay([...this.depotsGeo]); }
  getCapacites(clientId?: number): Promise<ICapacite[]> {
    if (clientId) return delay(this.capacites.filter(c => c.ClientId === clientId));
    return delay([...this.capacites]);
  }
  getClientSynthese(): Promise<IClientSynthese[]> { return delay([...this.synthese]); }

  // ---- Products config ----
  getProductsConfig(): Promise<IProductsConfig> { return delay(this.productsConfig); }
  saveProductsConfig(config: IProductsConfig): Promise<void> {
    this.productsConfig = config;
    return delay(undefined);
  }

  // ---- Bac actions (in-memory history) ----
  private bacActions: IBacAction[] = [];

  getBacActions(filter?: { bacId?: number; bacIds?: number[]; type?: string }): Promise<IBacAction[]> {
    let result = [...this.bacActions];
    if (filter?.bacId !== undefined) {
      result = result.filter(a => a.BacId === filter.bacId);
    } else if (filter?.bacIds && filter.bacIds.length > 0) {
      const set = new Set(filter.bacIds);
      result = result.filter(a => set.has(a.BacId));
    }
    if (filter?.type) result = result.filter(a => a.Type === filter.type);
    return delay(result.sort((a, b) => b.DoneAt.localeCompare(a.DoneAt)));
  }

  createBacAction(payload: IBacActionCreatePayload, file?: File): Promise<IBacAction> {
    const action: IBacAction = {
      Id: this.genId(),
      BacId: payload.bacId,
      Type: payload.type,
      Status: payload.status,
      Comment: payload.comment,
      DoneAt: payload.doneAt,
      PlanningEntryId: payload.planningEntryId ?? null,
      DoneByUserId: null,
      AttachmentName: file ? file.name : null,
      AttachmentMime: file ? file.type : null,
      AttachmentSize: file ? file.size : null,
      HasAttachment: !!file,
      CreatedAt: new Date().toISOString(),
    };
    this.bacActions.push(action);

    // Mirror server-side side-effects (flip bac flag on closed statuses).
    const isClosed = payload.status === 'confirme' || payload.status === 'replanifie';
    if (isClosed) {
      const idx = this.bacs.findIndex(b => b.Id === payload.bacId);
      if (idx >= 0) {
        if (payload.type === 'relance_depot') {
          this.bacs[idx] = { ...this.bacs[idx], PointDepot: true };
        } else if (payload.type === 'info_clients') {
          this.bacs[idx] = { ...this.bacs[idx], InfoClients: true };
        }
      }
    }

    // Optional intervention shift.
    if (payload.planningEntryId && payload.newInterventionMonth) {
      const idx = this.planning.findIndex(e => e.Id === payload.planningEntryId);
      if (idx >= 0) {
        this.planning[idx] = { ...this.planning[idx], Mois: payload.newInterventionMonth };
      }
    }

    return delay(action);
  }

  deleteBacAction(id: number): Promise<void> {
    this.bacActions = this.bacActions.filter(a => a.Id !== id);
    return delay(undefined);
  }

  getBacActionAttachmentUrl(): string {
    // Mock mode doesn't persist files on disk — return a placeholder.
    return '#';
  }
}
